import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { Component, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginatorIntl, PageEvent } from '@angular/material/paginator';
import { Router } from '@angular/router';
import { CustomActionService } from '@core/services/custom-action.service';
import { MetadataService } from '@core/services/metadata.service';
import { HubioTableComponent, HubioTableOptions } from '@hubio/hubio-common-components';
import { DashboardService } from '@modules/dashboard/services/dashboard.service';
import { PolicyCreationService } from '@modules/dashboard/services/policy.service';
import { DialogForm } from '@modules/policy/shared/components/dialog-form/dialog-form.component';
import { JsonFormFetcherService } from '@modules/policy/services/shared/json-form-fetcher.service';
import { TableConfig } from '@shared/models/feMetadata.model';
import { FormTabService } from 'form-tab';
import { forkJoin, Observable, of, Subscription } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import { PoliciesInForcePaginator } from './policies-in-force.paginator';

interface InForceActions {
  edit: boolean;
  cancel: boolean;
}

@Component({
  selector: 'app-policies-in-force',
  templateUrl: './policies-in-force.component.html',
  styleUrls: ['./policies-in-force.component.scss'],
  providers: [
    { provide: MatPaginatorIntl, useClass: PoliciesInForcePaginator }
  ]
})
export class PoliciesInForceComponent implements OnInit {
  @ViewChild('table', { static: false }) public table: HubioTableComponent;

  tableOptions: HubioTableOptions;
  tableConfig: TableConfig;
  getPoliciesSub: Subscription;
  transactionsHeight: string;
  pifConfig: any;
  policyNumberControl = new FormControl('');
  hasSearched = false;

  constructor(
    private policyService: PolicyCreationService,
    private metadataService: MetadataService,
    private dashboardService: DashboardService,
    private dialog: MatDialog,
    private jsonFormFetcherService: JsonFormFetcherService,
    private formTabService: FormTabService,
    private customActionService: CustomActionService,
    private router: Router
  ) { }

  ngOnInit() {
    this.initMetadata();
    this.initTableOptions();
    this.policyNumberControl.valueChanges.subscribe(() => this.updatePaginatorState());
  }

  ngAfterContentChecked(): void {
    if (this.transactionsHeight) return;
    const setHeight = (230 - 25)
      + (this.dashboardService.hasTabs() ? 50 : 0)
    this.transactionsHeight = `calc(100vh - ${setHeight}px)`;
    this.tableOptions.minHeight = this.transactionsHeight;
    this.updatePaginatorState();
  }

  initTableOptions() {
    this.tableOptions = {
      columns: this.tableConfig.columns,
      tooltip: this.tableConfig.tooltip,
      minHeight: this.transactionsHeight,
      paginator: {
        paginate: true,
        pageSize: this.dashboardService.PAGE_SIZE,
        length: 1
      },
      cache: {
        cache: true,
        maxCacheSize: this.pifConfig.maxPagesCached
      }
    };
  }

  initMetadata() {
    this.pifConfig = this.metadataService.getPoliciesInForceMetaData();
    this.tableConfig = this.pifConfig.tableDefinition;
  }

  getInForceByPolicyKey() {
    const policyKey = this.policyNumberControl.value?.trim();
    this.getPoliciesSub = this.policyService.getInForceByPolicyKey(policyKey).subscribe(policies => {
      const rows = this.withActions(policies);
      this.table.setData(rows, 0);
      this.tableOptions.paginator.length = Math.max(rows.length, 1);
    });
  }

  endorsePolicy(row: any) {
    this.openInForceActionModal(row, 'start-endorsement');
  }

  cancelPolicy(row: any) {
    this.openInForceActionModal(row, 'cancel-policy');
  }

  handlePaginationEvent(_event: PageEvent) {
    // Policy key search returns the full result set in one request.
  }

  handlePolicyNumberSearch() {
    if (!this.canSearch()) return;
    this.hasSearched = true;
    this.resetTableAndFetch()
  }

  clearPolicyNumberSearch() {
    this.policyNumberControl.setValue('');
    this.hasSearched = false;
    this.table?.setData([], 0);
    this.tableOptions.paginator.length = 1;
    this.updatePaginatorState();
  }

  canSearch(): boolean {
    const policyNumber = this.policyNumberControl.value?.trim();
    return !!policyNumber;
  }

  private openInForceActionModal(row: any, actionKey: string) {
    const actionConfig = this.pifConfig?.customActions?.[actionKey];
    if (!actionConfig?.modal) return;

    const formValues = {
      policyExpiryDate: row.policyExpiryDate ?? null
    };

    this.jsonFormFetcherService.getCommonConfig(actionConfig.modal.formName).pipe(
      mergeMap(config => this.resolveCommonLookups(config)),
      mergeMap(config => this.formTabService.initForm(JSON.stringify(config), JSON.stringify(formValues)))
    ).subscribe(res => {
      this.dialog.open(DialogForm, {
        panelClass: 'custom-action-dialog',
        data: {
          form: res.form,
          formJson: res.orderedFormJson
        }
      }).afterClosed().subscribe(dialogResult => {
        if (!(dialogResult instanceof FormGroup)) return;

        const payload = this.flattenFormPayload(dialogResult.getRawValue());
        delete payload['policyExpiryDate'];

        this.customActionService.executeWorkbenchAction(
          payload,
          actionConfig.endpoint,
          { policyKey: row.policyKey }
        ).subscribe(
          (response) => {
            if (actionKey === 'start-endorsement' || actionKey === 'cancel-policy') {
              this.openCreatedQuote(response);
              return;
            }
            this.customActionService.displaySuccessDialog(actionConfig.successMessage, response).subscribe(() => {
              if (this.hasSearched) {
                this.getInForceByPolicyKey();
              }
            });
          }
        );
      });
    });
  }

  private openCreatedQuote(response: any) {
    const created = response?.data ?? response;
    const { PolicyID, ClientID, PolicyLineOfBusinessCd } = created || {};
    if (!PolicyID || !ClientID) return;

    const lobPath = PolicyLineOfBusinessCd === 'A' ? 'auto' : 'hab';
    this.router.navigateByUrl(
      `/policy/${PolicyID}/${lobPath}/client/${ClientID}/client-information`,
      { state: { newPolicy: true } }
    );
  }

  // These lookups are served as common assets because the quote-editor lookup endpoint
  // requires a quoteId, which Policies in Force does not have.
  private resolveCommonLookups(config: any): Observable<any> {
    const fields = (Array.isArray(config) ? config : [])
      .reduce((all, form) => all.concat(form.parameters || []), [])
      .filter(field => field.lookup && !field.options);

    if (!fields.length) return of(config);

    const lookupNames = Array.from(new Set<string>(fields.map(field => field.lookup)));

    return forkJoin(lookupNames.map(name => this.jsonFormFetcherService.getCommonConfig(name))).pipe(
      map(results => {
        fields.forEach(field => {
          field.options = results[lookupNames.indexOf(field.lookup)] || [];
        });
        return config;
      })
    );
  }

  private flattenFormPayload(rawForm: object): object {
    let payload = {};
    for (const key in rawForm) {
      payload = {
        ...payload,
        ...rawForm[key]
      };
    }
    return payload;
  }

  private updatePaginatorState() {
    if (!this.table?.paginator) return;
    this.table.paginator.disabled = !this.hasSearched;
  }

  private resetTableAndFetch() {
    this.table.resetCache()
    this.table.paginator.firstPage()
    this.tableOptions.paginator.length = 1
    this.getInForceByPolicyKey()
  }

  private withActions(response: any): any[] {
    let rows: any[] = [];

    if (Array.isArray(response)) {
      rows = response;
    } else if (Array.isArray(response?.externalPolicies)) {
      rows = response.externalPolicies;
    } else if (response) {
      rows = [response];
    }

    return rows.map(row => ({
      ...row,
      Actions: this.buildInForceActions(row)
    }));
  }

  private buildInForceActions(row: any): InForceActions {
    const customActions = this.pifConfig?.customActions ?? {};
    const actions: InForceActions = {
      edit: !!customActions['start-endorsement'],
      cancel: !!customActions['cancel-policy'],
    };
    console.log(row);
    if (row.status === '7') {
      actions.edit = false;
      actions.cancel = false;
    }

    return actions;
  }

  ngOnDestroy(): void {
    this.getPoliciesSub?.unsubscribe()
  }
}